import type { SearchResultV5 } from '@seek/chalice-types';
import type { AxiosError } from 'axios';
import get from 'lodash/get';

import { isTimeoutError } from 'src/modules/seek-api-request';
import { hashToUserQueryId } from 'src/modules/seek-jobs-analytics';
import createApiClient from 'src/modules/seek-jobs-api-client';

import {
  selectBaseKeywords,
  selectJobSearchAssistState,
  selectLocale,
  selectPageSize,
  selectSessionId,
  selectUserClientId,
  selectUserTestHeaders,
  selectZoneFeatures,
  selectSearchSource,
} from '../selectors';
import type { TypedThunkAction } from '../types';

import { FETCH_SEARCH_RESULTS_ERROR } from './types';

const api = createApiClient();

export const fetchSearchResults =
  ({
    apiQuery = {},
    brand,
    cookies,
    country = 'AU',
    isAuthenticated = false,
    isSQCEnabled = false,
    newSince,
    relatedSearchesCount,
    serverProps,
    timeout,
    tries = 2,
    userAgent,
    userQueryId,
    visitorId,
    zone = 'anz-1',
  }: any): TypedThunkAction<Promise<SearchResultV5>> =>
  (dispatch, getState) => {
    const state = getState();

    const baseKeywords = selectBaseKeywords(state);
    const locale = selectLocale(state);
    const pageSize = selectPageSize(state);
    const sessionId = selectSessionId(state);
    const testHeaders = selectUserTestHeaders(state);
    const userClientId = selectUserClientId(state);
    const jobSearchAssistState = selectJobSearchAssistState(state);
    const source = selectSearchSource(state);

    const isJobSearchAssistEnabled =
      selectZoneFeatures(state)?.JOBSEARCH_ASSIST;

    const isNudgeIntentEnabled =
      selectZoneFeatures(state)?.NUDGE_INTENT ||
      cookies['feature_experiment-in-flow-nudging'] === 'in-flow-nudging';

    return api.jobs
      .search({
        baseKeywords,
        brand,
        cookies,
        country,
        isAuthenticated,
        isJobSearchAssistEnabled,
        /*
         * Given the feature flag from launch darkly is not accessible
         * in the api layer from the server side, zone features is being used
         * instead to conditionally include the relevant api params for nudge intent.
         *
         *  Note: We will still use the launch darkly feature flag on the client side
         *  to conditionally render the nudge intent component.
         */
        isNudgeIntentEnabled,
        isSQCEnabled,
        locale,
        newSince,
        relatedSearchesCount,
        requestId: state.location.requestId,
        searchParams: { ...apiQuery, pageSize },
        seekerId: get(state, 'user.seekerId'),
        serverProps,
        sessionId,
        shouldIncludeQueryHints: state.results.shouldIncludeQueryHints,
        solId: visitorId,
        source,

        // the stateToken is reused if the keywords are the same
        // this is to prevent the stateToken from being reused when the user
        // presses the back/forward button in the browser
        // and the keywords are the same as the previous search
        stateToken:
          jobSearchAssistState?.keywords === apiQuery.keywords
            ? jobSearchAssistState?.stateToken
            : undefined,
        testHeaders,
        timeout,
        userAgent,
        userClientId,
        userQueryId,
        zone,
      })
      .catch((err: AxiosError) => {
        dispatch({
          type: FETCH_SEARCH_RESULTS_ERROR,
        });

        const retryUserQueryId = hashToUserQueryId({
          query: apiQuery.normalisedQuery,
        });

        if (get(err, 'response.status') === 400) {
          return {
            totalCount: 0,
            data: [],
          } as any;
        }

        if (isTimeoutError(err) || get(err, 'response.status', 0) >= 500) {
          if (tries === 1) {
            throw err;
          }

          return fetchSearchResults({
            apiQuery,
            country,
            zone,
            cookies,
            userQueryId: retryUserQueryId,
            visitorId,
            tries: tries - 1,
            serverProps,
            isAuthenticated,
            newSince,
          })(dispatch, getState, null);
        }

        throw err;
      });
  };
